import { PromoListItem } from "@/core/entities/promotion/PromoListItem";
import { SubmitPromotionDocumentParams } from "@/core/repositories/promotion";
import { GetPromotionsUseCase, SubmitPromotionDocumentUseCase } from "@/core/usecases/promotion";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl/GraphQl.adapter";
import { PromotionDocumentUploadRepository } from "@/infrastructure/repositories/promotion/PromotionDocumentUpload.repository";
import { PromotionRepository } from "@/infrastructure/repositories/promotion/Promotion.repository";
import { AppConfig } from "@/infrastructure/services/config/WebApp.config";
import { atom } from "jotai";
import {
  getEmptyPromotionsFilterInfo,
  PromotionsFilterInfo,
  resolvePromotionsSortParams,
} from "./promotionFilter.utils";
import {
  PromotionDrawerInfo,
  promotionDrawerInfoAtom,
  promotionDrawerNavigationAtom,
  promotionsAtom,
  promotionsCurrentPageAtom,
  promotionsFilterInfoAtom,
  promotionsPageLimitAtom,
} from "./promotion.atoms";

const client = GraphQlAdapter.getInstance().getClient();
const _appConfig = AppConfig.getInstance();
const promotionRepository = new PromotionRepository(client);
const TIME_ZONE = _appConfig.TIMEZONE;

export const getPromotionsAtom = atom(
  null,
  async (
    get,
    set,
    { isLoading = true, isRefreshing = false }: { isLoading?: boolean; isRefreshing?: boolean } = {}
  ) => {
    try {
      set(promotionsAtom, {
        ...get(promotionsAtom),
        isLoading,
        isRefreshing,
        error: null,
        filterInfo: {
          ...get(promotionsAtom).filterInfo,
          lastFilteredAt: new Date(),
        },
      });

      const filterInfo = get(promotionsFilterInfoAtom);
      const currentPage = get(promotionsCurrentPageAtom);
      const pageLimit = get(promotionsPageLimitAtom);
      const { sortColumn, sortOrder } = resolvePromotionsSortParams(
        filterInfo.sortColumn,
        filterInfo.sortOrder
      );

      const response = await new GetPromotionsUseCase(promotionRepository).execute(
        {
          search: filterInfo.search ?? "",
          purchaser_ids: filterInfo.purchaser.map(p => Number(p.value)),
          promo_type_ids: filterInfo.promoType.map(p => Number(p.value)),
          promo_status_ids: filterInfo.promoStatus.map(p => Number(p.value)),
          start_from_date: filterInfo.startFromDate ?? "",
          start_to_date: filterInfo.startToDate ?? "",
          page_number: currentPage,
          page_size: pageLimit,
          sort_column: sortColumn,
          sort_order: sortOrder,
          time_zone: TIME_ZONE,
        },
        isLoading || isRefreshing
      );

      set(promotionsAtom, {
        ...get(promotionsAtom),
        promotions: response.items.map(item => new PromoListItem(item)),
        totalCount: response.total,
        currentPage: response.page,
        pageLimit: response.limit,
        isLoading: false,
        isRefreshing: false,
        error: null,
      });
    } catch (error) {
      set(promotionsAtom, {
        ...get(promotionsAtom),
        isLoading: false,
        isRefreshing: false,
        error: error as Error,
      });
    }
  }
);

export const setPromotionsFilterAtom = atom(null, (get, set, filter: PromotionsFilterInfo) => {
  set(promotionsAtom, {
    ...get(promotionsAtom),
    filterInfo: filter,
  });
});

export const clearPromotionsFilterAtom = atom(null, (get, set) => {
  set(promotionsAtom, {
    ...get(promotionsAtom),
    isFilterApplied: false,
    filterInfo: getEmptyPromotionsFilterInfo(),
  });
});

export const setPromotionsIsFilterAppliedAtom = atom(null, (get, set, isFilterApplied: boolean) => {
  set(promotionsAtom, {
    ...get(promotionsAtom),
    isFilterApplied,
  });
});

export const setPromotionsCurrentPageAtom = atom(null, (get, set, page: number) => {
  set(promotionsAtom, {
    ...get(promotionsAtom),
    currentPage: page,
  });
});

export const setPromotionDrawerInfoAtom = atom(null, (get, set, promotionDrawerInfo: PromotionDrawerInfo) => {
  set(promotionDrawerInfoAtom, {
    ...get(promotionDrawerInfoAtom),
    ...promotionDrawerInfo,
  });
});

export const setPromotionIsFirstItemAtom = atom(null, (get, set, isFirstItem: boolean) => {
  set(promotionDrawerNavigationAtom, {
    ...get(promotionDrawerNavigationAtom),
    isFirstItem,
  });
});

export const setPromotionIsLastItemAtom = atom(null, (get, set, isLastItem: boolean) => {
  set(promotionDrawerNavigationAtom, {
    ...get(promotionDrawerNavigationAtom),
    isLastItem,
  });
});

export const setPromotionIsNextPrevEnabledAtom = atom(null, (get, set, isNextPrevEnabled: boolean) => {
  set(promotionDrawerNavigationAtom, {
    ...get(promotionDrawerNavigationAtom),
    isNextPrevEnabled,
  });
});

export { isDefaultPromotionsFilter } from "./promotionFilter.utils";

export const submitPromotionDocumentAtom = atom(
  null,
  async (_get, _set, params: SubmitPromotionDocumentParams) => {
    const repo = new PromotionDocumentUploadRepository();
    return new SubmitPromotionDocumentUseCase(repo).execute(params);
  }
);
